import type { ComponentProps, CSSProperties, ReactNode, Ref } from 'react'
import { useTranslation } from 'react-i18next'
import Close from '#components/primitives/icons/close.tsx'
import { LoadingSpinner } from '#ynt-design-system/components/loading-spinner/index.ts'
import { cn } from '#core/utils/cn.ts'
import { settingClass } from '#ynt-design-system/settings.ts'

export type NoticeType = 'neutral' | 'success' | 'warning' | 'error' | 'loading'

const TYPE_CLASS: Record<NoticeType, string> = {
  neutral: 'ynt-notice-neutral',
  success: 'ynt-notice-success',
  warning: 'ynt-notice-warning',
  error: 'ynt-notice-error',
  loading: 'ynt-notice-loading',
}

export interface NoticeAction {
  label: string
  onClick: () => void
}

export interface NoticeProps extends Omit<ComponentProps<'div'>, 'ref' | 'children'> {
  type?: NoticeType
  action?: NoticeAction
  /** Renders a trailing dismiss button that calls this. */
  onDismiss?: () => void
  /** Milliseconds; draws a line along the bottom edge that drains over this time. */
  countdown?: number
  /** Freezes the countdown line. */
  paused?: boolean
  ref?: Ref<HTMLDivElement>
  children: ReactNode
}

export function Notice({
  type = 'neutral',
  action,
  onDismiss,
  countdown,
  paused = false,
  className,
  style,
  children,
  ref,
  ...rest
}: NoticeProps) {
  // Rendered by the app-root provider, so a namespace still loading must not suspend the whole app.
  const { t } = useTranslation('general', { useSuspense: false })
  const loading = type === 'loading'
  const trailing = !loading && (action || onDismiss)

  return (
    <div
      ref={ref}
      className={cn('ynt-notice', settingClass('theme', 'dark'), TYPE_CLASS[type], !trailing && 'ynt-notice-bare', className)}
      style={countdown ? ({ ...style, '--notice-countdown': `${countdown}ms` } as CSSProperties) : style}
      {...rest}
    >
      {loading && <LoadingSpinner className="ynt-notice-icon" />}
      <span className="ynt-notice-message">{children}</span>
      {!loading && action && (
        <button type="button" className="ynt-notice-action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
      {!loading && onDismiss && (
        <button
          type="button"
          className="ynt-notice-dismiss"
          aria-label={t('shared.common.dismiss')}
          onClick={onDismiss}
        >
          <Close aria-hidden="true" />
        </button>
      )}
      {!loading && countdown ? (
        <span className={cn('ynt-notice-timer', paused && 'ynt-notice-paused')} aria-hidden="true" />
      ) : null}
    </div>
  )
}

export default Notice
